import { NOOP_LAYOUT_MANAGER, setNoopLayoutManager, recalcTableCells } from './tableUtils';

/**
 * 그룹 자식 구조로 TextBoxGroup/TableGroup 감지 및 속성 복원
 * fabric v7은 생성자/loadFromJSON에서 커스텀 속성을 설정하지 않으므로
 * 자식 구조를 분석하여 그룹 타입과 메타데이터를 추론한다.
 * @param {fabric.Group} obj
 */
export const inferGroupType = (obj) => {
  const children = obj.getObjects ? obj.getObjects() : [];
  if (children.length === 0) return;

  // TextBoxGroup: 자식 2개 (Rect + Textbox)
  if (children.length === 2 && children[0].type === 'rect' && ['textbox', 'i-text', 'text'].includes(children[1].type)) {
    obj._isTextBoxGroup = true;
    const rect = children[0];
    obj._bgColor = typeof rect.fill === 'string' ? rect.fill : '#ffffff';
    obj._bgAlpha = obj._bgAlpha ?? Math.round((rect.opacity ?? 1) * 100);
    obj._bgShape = 'rect';
    // 테두리 복원
    if (rect.stroke && rect.strokeWidth > 0) {
      obj._borderColor = obj._borderColor || (typeof rect.stroke === 'string' ? rect.stroke : '#000000');
      obj._borderWidth = obj._borderWidth || rect.strokeWidth;
      obj._borderOpacity = obj._borderOpacity ?? 100;
    }
    return;
  }

  // TableGroup: 4+개 자식, Rect/Textbox 쌍 반복
  if (children.length >= 4 && children.length % 2 === 0) {
    const isTablePattern = children.every((c, i) =>
      i % 2 === 0 ? c.type === 'rect' : ['textbox', 'i-text', 'text'].includes(c.type)
    );
    if (!isTablePattern) return;

    obj._isTableGroup = true;
    const rects = children.filter((_, i) => i % 2 === 0);
    const texts = children.filter((_, i) => i % 2 === 1);

    // 병합 셀 대응: 중심점 대신 실제 가장자리(edge)로 그리드 라인 계산
    const edges = rects.map(r => {
      const w = (r.width || 0) * (r.scaleX || 1);
      const h = (r.height || 0) * (r.scaleY || 1);
      return {
        left: Math.round(r.left - w / 2),
        right: Math.round(r.left + w / 2),
        top: Math.round(r.top - h / 2),
        bottom: Math.round(r.top + h / 2),
      };
    });

    // 수직 그리드 라인 (좌/우 경계)
    const vLineSet = new Set();
    edges.forEach(e => { vLineSet.add(e.left); vLineSet.add(e.right); });
    const vLines = [...vLineSet].sort((a, b) => a - b);
    const cols = vLines.length - 1;

    // 수평 그리드 라인 (상/하 경계)
    const hLineSet = new Set();
    edges.forEach(e => { hLineSet.add(e.top); hLineSet.add(e.bottom); });
    const hLines = [...hLineSet].sort((a, b) => a - b);
    const rows = hLines.length - 1;

    obj._tableRows = rows;
    obj._tableCols = cols;
    obj._tableColWidths = [];
    for (let i = 0; i < cols; i++) obj._tableColWidths.push(vLines[i + 1] - vLines[i]);
    obj._tableRowHeights = [];
    for (let i = 0; i < rows; i++) obj._tableRowHeights.push(hLines[i + 1] - hLines[i]);

    rects.forEach((rect, idx) => {
      const e = edges[idx];
      const colStart = vLines.indexOf(e.left);
      const colEnd = vLines.indexOf(e.right);
      const rowStart = hLines.indexOf(e.top);
      const rowEnd = hLines.indexOf(e.bottom);

      rect._isTableCell = true;
      rect._cellRow = rowStart;
      rect._cellCol = colStart;
      rect._gridSpan = Math.max(1, colEnd - colStart);
      rect._rowSpan = Math.max(1, rowEnd - rowStart);

      if (texts[idx]) {
        texts[idx]._isTableCellText = true;
        texts[idx]._cellRow = rowStart;
        texts[idx]._cellCol = colStart;
      }
    });
  }
};

/**
 * loadFromJSON 후 JSON에서 커스텀 속성 복원 + 구조 기반 추론 fallback
 * @param {fabric.Canvas} canvas
 * @param {Object} jsonState - JSON.parse된 캔버스 상태
 */
export const restoreCustomProps = (canvas, jsonState) => {
  const jsonObjects = jsonState?.objects || [];
  canvas.getObjects().forEach((obj, i) => {
    const jsonObj = jsonObjects[i] || {};

    // JSON에서 커스텀 속성 복원
    if (jsonObj.id) obj.id = jsonObj.id;
    if (jsonObj.name) obj.name = jsonObj.name;
    if (jsonObj._layerExpanded !== undefined) obj._layerExpanded = jsonObj._layerExpanded;

    if (jsonObj._isTextBoxGroup) {
      obj._isTextBoxGroup = true;
      obj._bgColor = jsonObj._bgColor;
      obj._bgAlpha = jsonObj._bgAlpha;
      obj._bgShape = jsonObj._bgShape;
      obj._borderColor = jsonObj._borderColor || null;
      obj._borderOpacity = jsonObj._borderOpacity ?? 100;
      obj._borderWidth = jsonObj._borderWidth || 0;
    }
    if (jsonObj._isTableGroup) {
      obj._isTableGroup = true;
      obj._tableRows = jsonObj._tableRows;
      obj._tableCols = jsonObj._tableCols;
      obj._tableColWidths = jsonObj._tableColWidths;
      obj._tableRowHeights = jsonObj._tableRowHeights;

      const childObjs = obj.getObjects ? obj.getObjects() : [];
      const jsonChilds = jsonObj.objects || [];
      childObjs.forEach((child, j) => {
        const jsonChild = jsonChilds[j] || {};
        if (jsonChild._isTableCell) child._isTableCell = true;
        if (jsonChild._isTableCellText) child._isTableCellText = true;
        if (jsonChild._cellRow !== undefined) child._cellRow = jsonChild._cellRow;
        if (jsonChild._cellCol !== undefined) child._cellCol = jsonChild._cellCol;
        if (jsonChild._gridSpan !== undefined) child._gridSpan = jsonChild._gridSpan;
        if (jsonChild._rowSpan !== undefined) child._rowSpan = jsonChild._rowSpan;
        if (jsonChild._marL !== undefined) child._marL = jsonChild._marL;
        if (jsonChild._marR !== undefined) child._marR = jsonChild._marR;
        if (jsonChild._marT !== undefined) child._marT = jsonChild._marT;
        if (jsonChild._marB !== undefined) child._marB = jsonChild._marB;
      });
    }

    // JSON에 플래그 없으면 자식 구조로 타입 추론
    if (obj.type === 'group' && !obj._isTextBoxGroup && !obj._isTableGroup) {
      inferGroupType(obj);
    }

    // textbox 속성 복원 (wordWrap, splitByGrapheme, 마진, 컨트롤 색상)
    const restoreTextboxProps = (textObj, jsonData) => {
      if (!textObj || !jsonData) return;
      if (jsonData.wordWrap !== undefined) textObj.wordWrap = jsonData.wordWrap;
      if (jsonData.splitByGrapheme !== undefined) textObj.splitByGrapheme = jsonData.splitByGrapheme;
      if (jsonData._marL !== undefined) textObj._marL = jsonData._marL;
      if (jsonData._marR !== undefined) textObj._marR = jsonData._marR;
      if (jsonData._marT !== undefined) textObj._marT = jsonData._marT;
      if (jsonData._marB !== undefined) textObj._marB = jsonData._marB;
      if (textObj.wordWrap === false) {
        textObj._wordJoiners = /\x00/;
        // _wordJoiners 설정 후 반드시 dimensions 재계산
        textObj.initDimensions();
      }
    };

    if (obj.type === 'textbox') {
      restoreTextboxProps(obj, jsonObj);
      // 컨트롤 색상 복원
      const color = obj.wordWrap ? '#22c55e' : '#3b82f6';
      obj.set({ borderColor: color, cornerColor: color });
    }

    // TextBoxGroup: 배경 Rect 비선택/비이벤트 + 자식 textbox 복원
    if (obj._isTextBoxGroup) {
      const childObjs = obj.getObjects ? obj.getObjects() : [];
      const jsonChilds = jsonObj.objects || [];
      childObjs.forEach((child, j) => {
        const jsonChild = jsonChilds[j] || {};
        if (child.type === 'rect') {
          child.set({ selectable: false, evented: false });
        }
        if (['textbox', 'i-text', 'text'].includes(child.type)) {
          restoreTextboxProps(child, jsonChild);
        }
      });
      // TextBoxGroup 컨트롤 색상: 내부 textbox의 wordWrap 기준
      const innerText = childObjs.find(c => ['textbox', 'i-text', 'text'].includes(c.type));
      const groupColor = innerText?.wordWrap ? '#22c55e' : '#3b82f6';
      obj.set({ borderColor: groupColor, cornerColor: groupColor });
    }

    // 모든 Group: NOOP 레이아웃 매니저 + 자식 textbox 속성 복원
    if (obj.type === 'group') {
      setNoopLayoutManager(obj);
      // TextBoxGroup/TableGroup이 아닌 일반 그룹의 자식 textbox도 복원
      if (!obj._isTextBoxGroup && !obj._isTableGroup) {
        const childObjs = obj.getObjects ? obj.getObjects() : [];
        const jsonChilds = jsonObj.objects || [];
        childObjs.forEach((child, j) => {
          const jsonChild = jsonChilds[j] || {};
          if (['textbox', 'i-text', 'text'].includes(child.type)) {
            restoreTextboxProps(child, jsonChild);
          }
        });
      }
    }

    // TableGroup: 셀 비선택/비이벤트 + subTargetCheck
    if (obj._isTableGroup) {
      obj.set({ subTargetCheck: true });
      // FitContentLayout이 시프트한 자식 위치를 올바르게 재배치
      if (obj._tableColWidths && obj._tableRowHeights) {
        recalcTableCells(obj);
      }
      (obj.getObjects ? obj.getObjects() : []).forEach(child => {
        if (child._isTableCell) {
          child.set({ selectable: false, evented: false });
        }
      });
    }
  });
};

/**
 * 직렬화된 JSON에서 layoutManager 제거 (재귀)
 * NOOP_LAYOUT_MANAGER는 Fabric.js classRegistry에 미등록 → loadFromJSON 실패 방지
 * restoreCustomProps에서 테이블 그룹에 NOOP을 다시 설정
 */
export const stripLayoutManagers = (state) => {
  const strip = (objects) => {
    if (!objects) return;
    objects.forEach(obj => {
      delete obj.layoutManager;
      if (obj.objects) strip(obj.objects);
    });
  };
  strip(state?.objects);
  return state;
};
